import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { grey25, navy } from '@zocdoc/branding/colors';
import { breakpoint } from '@zocdoc/components/src/grid';
import { FontFamily } from '@zocdoc/components/src/typography';

import {
    HEADER_BAR_HEIGHT_XXSMALL,
    HEADER_BAR_HEIGHT_MEDIUM,
} from '../../../constants';

const Header = ({
    numberOfAppliedRefinements,
    onCloseCallback,
    potentialMatches,
    resetCallback,
}) => (
    <div data-test="search-refine-panel-header">
        <Top>
            <MobileTitle>
                <FormattedMessage
                    defaultMessage="refine"
                    id="Components.RefineSearch.Header.Title"
                />

                {numberOfAppliedRefinements > 0 ? (
                    <MobileTitleRefinementCount>
                        <FormattedMessage
                            defaultMessage={'({count})'}
                            id="Components.RefineSearch.Header.AppliedRefinementCount"
                            values={{ count: numberOfAppliedRefinements }}
                        />
                    </MobileTitleRefinementCount>
                ) : null}
            </MobileTitle>

            {renderMatches(DesktopMatches, potentialMatches)}

            <Cancel
                data-test="search-refine-panel-header-back"
                onClick={onCloseCallback}
            >
                <FormattedMessage
                    defaultMessage="done"
                    id="Components.RefineSearch.Header.Back"
                />
            </Cancel>

            {numberOfAppliedRefinements > 0 ? (
                <Reset
                    data-test="search-refine-panel-header-reset"
                    onClick={resetCallback}
                >
                    <FormattedMessage
                        defaultMessage="reset"
                        id="Components.RefineSearch.Header.Reset"
                    />
                </Reset>
            ) : null}
        </Top>

        {renderMatches(MobileMatches, potentialMatches)}
    </div>
);

Header.propTypes = {
    numberOfAppliedRefinements: PropTypes.number,
    onCloseCallback: PropTypes.func.isRequired,
    potentialMatches: PropTypes.number,
    resetCallback: PropTypes.func.isRequired,
};

const renderMatches = (Component, count) => (
    <Component data-test="search-refine-match-count">
        <FormattedMessage
            defaultMessage={`{count, plural,
                =1 {# match}
                other {# matches}
            }`}
            id="Components.RefineSearch.Matches"
            values={{ count }}
        />
    </Component>
);

const buttonCSS = css`
    background: none;
    border: 0;
    border-bottom: 1px dotted;
    color: inherit;
    cursor: pointer;
    font: 13px ${FontFamily.SharpSansSemibold};
    line-height: 1.3;
    padding: 0;
    text-align: center;
`;

const Cancel = styled.button`
    ${buttonCSS}
    order: 3;

    ${breakpoint.small} {
        display: none;
    }
`;

const Reset = styled.button`
    ${buttonCSS}
    order: -1;

    ${breakpoint.small} {
        order: 1;
    }
`;

const MobileTitle = styled.div`
    font: 18px ${FontFamily.SharpSansBold};

    ${breakpoint.small} {
        display: none;
    }
`;

const MobileTitleRefinementCount = styled.span`
    font: 14px ${FontFamily.SharpSansSemibold};
    margin-left: 4px;
`;

const matchesCSS = css`
    font: 15px/1 ${FontFamily.SharpSansBold};
    letter-spacing: 0.4px;
    text-align: center;
`;

const MobileMatches = styled.div`
    ${matchesCSS}
    align-items: center;
    background-color: ${grey25};
    color: ${navy};
    display: flex;
    font: 15px/1 ${FontFamily.SharpSansBold};
    height: ${HEADER_BAR_HEIGHT_XXSMALL}px;
    letter-spacing: 0.4px;
    justify-content: center;

    ${breakpoint.small} {
        display: none;
    }
`;

const DesktopMatches = styled.div`
    ${matchesCSS}
`;

const Top = styled.div`
    align-items: center;
    background-color: ${p => p.theme.search.header.filters.panel.header.bgColor};
    box-sizing: border-box;
    color: ${p => p.theme.search.header.filters.panel.header.textColor};
    display: flex;
    justify-content: space-between;
    padding: 0 30px;
    width: 100%;
    height: ${HEADER_BAR_HEIGHT_XXSMALL}px;

    > * {
        flex-shrink: 0;
    }

    ${breakpoint('xsmall', { down: true })} {
        ${DesktopMatches} {
            display: none;
        }
    }

    ${breakpoint.medium} {
        height: ${HEADER_BAR_HEIGHT_MEDIUM}px;
    }
`;

export default Header;
